import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { UploadSimple, DownloadSimple, Trash, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const DOC_TYPES = ["aadhaar", "pan", "resume", "offer_letter", "appointment", "experience", "payslip", "other"];

export default function Documents() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ employee_id: "", doc_type: "resume", expiry_date: "" });

  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);
  const BACKEND = typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    if (!activeCompanyId) return;
    const [d, e] = await Promise.all([
      api.get("/documents", { params: { company_id: activeCompanyId } }),
      api.get("/employees", { params: { company_id: activeCompanyId, limit: 500 } }),
    ]);
    setDocs(d.data);
    setEmployees(e.data.items);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const upload = async () => {
    if (!file || !form.employee_id) { toast.error("Choose employee and file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("employee_id", form.employee_id);
    fd.append("company_id", activeCompanyId);
    fd.append("doc_type", form.doc_type);
    if (form.expiry_date) fd.append("expiry_date", form.expiry_date);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false); setFile(null);
      setForm({ employee_id: "", doc_type: "resume", expiry_date: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));

  return (
    <div data-testid="documents-page">
      <PageHeader eyebrow={activeCompany?.code} title="Documents" subtitle="Securely store employee files.">
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="upload-doc-btn">
                <UploadSimple size={14} className="mr-1.5" /> Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase">Employee</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="doc-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase">Type</Label>
                  <Select value={form.doc_type} onValueChange={(v) => setForm({...form, doc_type: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase">File</Label>
                  <input type="file" onChange={(e) => setFile(e.target.files?.[0])} className="block w-full text-xs mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-xs file:bg-[#2563EB] file:text-white" data-testid="doc-file" />
                </div>
              </div>
              <DialogFooter><Button onClick={upload} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="submit-upload">Upload</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>
      <PageBody>
        <Section title={`${docs.length} documents`} dense>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>File</TableHead>
              <TableHead>Size</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {docs.map((d) => {
                const e = empMap[d.employee_id] || {};
                return (
                  <TableRow key={d.id} data-testid={`doc-${d.id}`}>
                    <TableCell>{e.full_name || "—"} <span className="text-xs font-mono text-[#64748B] ml-2">{e.employee_code}</span></TableCell>
                    <TableCell className="text-xs uppercase tracking-wider">{d.doc_type}</TableCell>
                    <TableCell className="flex items-center gap-2"><FileText size={14} /><span className="text-sm">{d.file_name}</span></TableCell>
                    <TableCell className="text-xs">{(d.file_size / 1024).toFixed(1)} KB</TableCell>
                    <TableCell className="text-xs">{d.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <a href={`${BACKEND}${d.file_url}`} target="_blank" rel="noreferrer" className="inline-block p-1.5 hover:bg-[#EFF6FF] rounded-sm" data-testid={`dl-${d.id}`}>
                        <DownloadSimple size={14} />
                      </a>
                      {canEdit && <button onClick={() => del(d.id)} className="p-1.5 hover:bg-[#F54236]/10 text-[#F54236] rounded-sm" data-testid={`del-doc-${d.id}`}><Trash size={14} /></button>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {docs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#64748B] py-8">No documents yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Section>
      </PageBody>
    </div>
  );
}
